import { ref, nextTick } from 'vue'

const BAR_COUNT = 10
const IDLE_LEVEL = 0.045
const ENERGY_FLOOR = 0.018
const ENERGY_CEILING = 0.32
const SPEECH_PROBABILITY_FLOOR = 0.45

function getVisualizerPalette() {
  const styles = getComputedStyle(document.documentElement)
  const getColor = (name, fallback) => styles.getPropertyValue(name).trim() || fallback

  return {
    start: getColor('--color-brand-700', '#0f7475'),
    middle: getColor('--color-brand-500', '#19aeb1'),
    end: getColor('--color-accent-300', '#8ee9dc'),
  }
}

function addAlphaToHex(color, alpha, fallback) {
  const match = color.match(/^#([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i)
  if (!match) return fallback

  const [, red, green, blue] = match
  return `rgba(${parseInt(red, 16)}, ${parseInt(green, 16)}, ${parseInt(blue, 16)}, ${alpha})`
}

function clampLevel(value) {
  return Math.max(0, Math.min(1, value || 0))
}

function mapSpeechProbability(value) {
  const gated = clampLevel((value - SPEECH_PROBABILITY_FLOOR) / (1 - SPEECH_PROBABILITY_FLOOR))
  return Math.pow(gated, 2.2) * 0.68
}

function getEnergyLevel(frame, start = 0, end = frame?.length || 0) {
  if (!frame?.length || end <= start) return 0

  let sum = 0
  let peak = 0

  for (let i = start; i < end; i++) {
    const sample = Math.abs(frame[i] || 0)
    sum += sample * sample
    if (sample > peak) peak = sample
  }

  const rms = Math.sqrt(sum / (end - start))
  const mixedEnergy = rms * 1.8 + peak * 0.08
  const gated = clampLevel((mixedEnergy - ENERGY_FLOOR) / (ENERGY_CEILING - ENERGY_FLOOR))
  return Math.pow(gated, 0.82)
}

function getFrameBarLevels(frame, fallbackLevel) {
  const center = (BAR_COUNT - 1) / 2

  return Array.from({ length: BAR_COUNT }, (_, index) => {
    const start = Math.floor((index / BAR_COUNT) * (frame?.length || 0))
    const end = Math.floor(((index + 1) / BAR_COUNT) * (frame?.length || 0))
    const segmentLevel = getEnergyLevel(frame, start, end)
    const centerWeight = 1 - (Math.abs(index - center) / center) * 0.18
    return clampLevel(Math.max(segmentLevel, fallbackLevel * 0.42) * centerWeight)
  })
}

export function useVadVisualizer({ readAlongMode, isRecording, audioVisualizerRef }) {
  const animationFrame = ref(null)
  const vadLevel = ref(0)
  const microphoneLevel = ref(0)
  const microphoneBars = ref(Array(BAR_COUNT).fill(IDLE_LEVEL))
  const barLevels = ref(Array(BAR_COUNT).fill(0))
  const smoothBarLevels = Array(BAR_COUNT).fill(IDLE_LEVEL)

  function setVadLevel(value, frame) {
    const speechLevel = mapSpeechProbability(value)
    const frameLevel = getEnergyLevel(frame)
    microphoneLevel.value = frameLevel
    vadLevel.value = Math.max(frameLevel, speechLevel * 0.28)
    barLevels.value = getFrameBarLevels(frame, vadLevel.value)
  }

  function visualize() {
    nextTick(() => {
      const canvas = audioVisualizerRef.value
      const canvasCtx = canvas?.getContext('2d') || null

      const palette = getVisualizerPalette()
      if (canvas && canvasCtx) resizeCanvas(canvas)

      const draw = () => {
        if (readAlongMode.value === 'following') {
          animationFrame.value = null
          return
        }

        animationFrame.value = requestAnimationFrame(draw)

        const listeningBoost = isRecording.value ? 1 : 0.72
        for (let i = 0; i < BAR_COUNT; i++) {
          const targetLevel = Math.max(IDLE_LEVEL, (barLevels.value[i] || 0) * listeningBoost)
          const easing = targetLevel > smoothBarLevels[i] ? 0.5 : 0.24
          smoothBarLevels[i] += (targetLevel - smoothBarLevels[i]) * easing
        }

        microphoneBars.value = [...smoothBarLevels]
        microphoneLevel.value = Math.max(...smoothBarLevels)

        if (canvas?.isConnected && canvasCtx) {
          resizeCanvas(canvas)
          drawGradientBars(canvasCtx, canvas.width, canvas.height, smoothBarLevels, palette)
        }
      }

      if (!animationFrame.value) draw()
    })
  }

  function resizeCanvas(canvas) {
    const ratio = window.devicePixelRatio || 1
    const width = Math.max(1, Math.floor(canvas.clientWidth * ratio))
    const height = Math.max(1, Math.floor(canvas.clientHeight * ratio))

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width
      canvas.height = height
    }
  }

  function drawGradientBars(canvasCtx, width, height, levels, palette) {
    canvasCtx.clearRect(0, 0, width, height)

    const ratio = window.devicePixelRatio || 1
    const barWidth = Math.max(3 * ratio, Math.min(4 * ratio, width / 32))
    const gap = Math.max(3 * ratio, Math.min(4 * ratio, width / 32))
    const totalWidth = BAR_COUNT * barWidth + (BAR_COUNT - 1) * gap
    const startX = (width - totalWidth) / 2
    const centerY = height / 2
    const maxHeight = height * 0.76
    const minHeight = Math.max(4 * ratio, height * 0.1)

    const gradient = canvasCtx.createLinearGradient(startX, 0, startX + totalWidth, 0)
    gradient.addColorStop(0, palette.start)
    gradient.addColorStop(0.5, palette.middle)
    gradient.addColorStop(1, palette.end)

    canvasCtx.fillStyle = gradient
    canvasCtx.shadowBlur = 8 * ratio
    canvasCtx.shadowColor = addAlphaToHex(palette.middle, 0.34, 'rgba(25, 174, 177, 0.34)')

    levels.forEach((level, index) => {
      const barHeight = Math.max(minHeight, Math.pow(clampLevel(level), 0.72) * maxHeight)
      const x = startX + index * (barWidth + gap)
      const y = centerY - barHeight / 2
      const radius = Math.min(barWidth / 2, 2 * ratio)

      canvasCtx.beginPath()
      if (typeof canvasCtx.roundRect === 'function') {
        canvasCtx.roundRect(x, y, barWidth, barHeight, radius)
      } else {
        canvasCtx.rect(x, y, barWidth, barHeight)
      }
      canvasCtx.fill()
    })

    canvasCtx.shadowBlur = 0
  }

  function stopVisualizer() {
    if (animationFrame.value) {
      cancelAnimationFrame(animationFrame.value)
      animationFrame.value = null
    }

    vadLevel.value = 0
    microphoneLevel.value = 0
    barLevels.value = Array(BAR_COUNT).fill(0)
    smoothBarLevels.fill(IDLE_LEVEL)
    microphoneBars.value = [...smoothBarLevels]
  }

  return {
    microphoneLevel,
    microphoneBars,
    setVadLevel,
    visualize,
    stopVisualizer,
  }
}
